import React, { ReactElement, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Select, Card, Spin, Divider, Table } from "antd";
import type { TableColumnsType } from "antd";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";
import NewInstalledProductModal from "components/new-installed-product-modal/new-installed-product-modal";
import NewProductModal from "components/new-product-modal/new-product-modal";
import MediaPlayer from "components/media-player/media-player";
import StructuredLocation from "components/structured-location/structured-location";

import { DeviceDetectionJobStatus } from "constants/field-values";
import routeNames from "constants/route-names";
import { getFileIcon } from "utils/files-management";
import { useDeviceDetectionCore } from "hooks/useDeviceDetectionCore";
import { usePropertyLocationLabels } from "hooks/usePropertyLocationLabels";
import { useProductCategories } from "hooks/useProductCategories";

/**
 * DeviceDetectionJob — Admin standalone screen.
 *
 * Lives under AdminNavigationLayout at:
 *   ADMIN_DEVICE_DETECTION_ADD     /admin-device-detection/add
 *   ADMIN_DEVICE_DETECTION_DETAILS /admin-device-detection/:jobId
 *
 */
const DeviceDetectionJob: React.FC = () => {
	const navigate = useNavigate();

	const { getLabelFromName } = useProductCategories();

	const handleOnBack = () => navigate(routeNames.ADMIN_DEVICE_DETECTIONS);

	const handleOnJobReady = (jobId: string) => {
		navigate(routeNames.ADMIN_DEVICE_DETECTION_DETAILS.replace(":jobId", jobId));
	};

	const core = useDeviceDetectionCore({
		onBack: handleOnBack,
		onJobReady: handleOnJobReady
	});

	const {
		selectedProperty,
		setSelectedProperty,
		attachmentsFiles,
		setAttachmentsFiles,
		setJobId,
		setProcessingStarted,
		addAttachmentModalOpen,
		setAddAttachmentModalOpen,
		addAttachmentModalEditMode,
		productModalOpen,
		selectedProduct,
		installedProductModalOpen,
		selectedInstallation,
		addProductModalOpen,
		fileViewer,
		setFileViewer,
		isLoading,
		propertiesLoading,
		propertyOptions,
		processingStarted,
		job,
		productsGroupedByProductType,
		processingData,
		handleOpenProductModal,
		handleCloseProductModal,
		handleConfirmProductDeletion,
		handleOpenInstalledProductModal,
		handleCloseInstalledProductModal,
		handleConfirmInstalledProductDeletion,
		handleOpenAddProductModal,
		handleCloseAddProductModal,
		handleOnCloseAttachmentModal,
		handleOnRemoveAttachment,
		updateAttachments,
		handleOnClickAttachment,
		handleOnSubmit,
		publishDetection
	} = core;

	// Draft installations only carry the building/floor/room ids; resolve their names from the
	// property's hierarchy so the structured location can be shown next to the free-text location.
	const { getLabels } = usePropertyLocationLabels(job?.property_id ?? selectedProperty?.id);

	const installationsTableColumns = useMemo(() => {
		return [
			{
				title: "#",
				dataIndex: "installed_device_index",
				key: "index",
				width: 60
			},
			{
				title: "Location",
				dataIndex: "location",
				key: "location",
				render: (location: any, record: any) => (
					<>
						<StructuredLocation labels={getLabels(record)} />
						{location || "-"}
					</>
				)
			},
			{
				title: "Serial #",
				dataIndex: "product_serial",
				key: "product_serial",
				minWidth: 100,
				render: (serial, record) => {
					if (!serial || serial === "null") return "-";

					let confidence: ReactElement | null = null;

					if (record.serial_number_confidence && record.serial_number_confidence !== "not_visible") {
						confidence = (
							<span className={`device-detection-confidence ${record.serial_number_confidence}`}>
								{record.serial_number_confidence}
							</span>
						);
					}

					return (
						<div className="device-detection-serial-group">
							<span className="device-detection-value device-detection-serial">{serial}</span>
							{confidence}
						</div>
					);
				}
			},
			{
				title: "Visible",
				dataIndex: "installation_visible",
				key: "visible",
				width: 100,
				render: visible => (
					<span className={`device-detection-badge ${visible ? "visible" : "not-visible"}`}>
						{visible ? "Yes" : "No"}
					</span>
				)
			},
			{
				title: "DOCUMENTS",
				dataIndex: "attachments",
				key: "DOCUMENTS",
				minWidth: 100,
				render: files => {
					if (!files || files.length === 0) return "-";
					return <span>{files.length}</span>;
				}
			},
			job?.status === DeviceDetectionJobStatus.DRAFT
				? {
						title: "",
						key: "actions",
						minWidth: 100,
						width: "auto",
						render: (_, record) => {
							const handleEditClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleOpenInstalledProductModal(record);
							};

							const handleDeleteClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleConfirmInstalledProductDeletion(record);
							};

							return (
								<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className={"selected-file-icon"}
										onClick={handleEditClick}
									/>
									<RemovePopconfirm
										title="Delete Installed Device"
										description={
											<>
												<span>Are you sure you want to delete this installed device?</span>
											</>
										}
										handleDeleteClick={handleDeleteClick}
									/>
								</div>
							);
						}
					}
				: {}
		];
	}, [handleConfirmInstalledProductDeletion, job?.status, getLabels]);

	const tableColumns = useMemo(
		() => [
			{
				title: "TYPE",
				dataIndex: "category",
				render: (_field: any, record: any) => getLabelFromName(record.category),
				key: "TYPE"
			},
			{
				title: "INSTALLED",
				dataIndex: "quantity",
				key: "INSTALLED"
			},
			{
				title: "CONFIDENCE",
				dataIndex: "confidence",
				render: (_field: any, record: any) => (
					<span className={`device-detection-confidence ${record.confidence}`}>{record.confidence}</span>
				),
				key: "CONFIDENCE"
			}
		],
		[getLabelFromName]
	);

	// ── Detection summary (admin-specific layout) ─────────────────────────────

	const renderDetectionSummary = () => {
		if (!job?.ai_response) return null;

		try {
			const response = typeof job.ai_response === "string" ? JSON.parse(job.ai_response) : job.ai_response;
			if (!response.detection_summary) return null;

			// Calculate categories from productsGroupedByProductType if available (edited state)
			let categories_detected = response.detection_summary.categories_detected;
			let totalInstalledProducts = 0;

			if (productsGroupedByProductType && productsGroupedByProductType.length > 0) {
				// Use current edited state
				categories_detected = productsGroupedByProductType
					.filter(group => group.totalProducts > 0)
					.map(group => ({
						category: group.product_type?.name || "UNCATEGORIZED",
						quantity: group.totalInstalledProducts,
						confidence: "high" // Confirmed products
					}));

				totalInstalledProducts = productsGroupedByProductType.reduce(
					(sum, group) => sum + (group.totalInstalledProducts || 0),
					0
				);
			} else {
				// Fallback to original AI response
				const installations =
					typeof job.detected_installed_products === "string"
						? JSON.parse(job.detected_installed_products)
						: job.detected_installed_products;
				totalInstalledProducts = installations?.length || 0;
			}

			const categoriesCount = categories_detected?.length ?? 0;
			const { total_devices_found } = response.detection_summary;
			const { name: propertyName } = job.property;
			const authorName = `${job.author?.first_name} ${job.author?.last_name}`;

			return (
				<div>
					<span className="device-detection-section-title">Detection Summary</span>
					<Card className="device-detection-summary-card">
						<div className="device-detection-row">
							<div className="device-detection-stat-item">
								<span className="device-detection-stat-label">Property:</span>
								<span className="device-detection-stat-value">{propertyName}</span>
							</div>
							<div className="device-detection-stat-item">
								<span className="device-detection-stat-label">Requested by:</span>
								<span className="device-detection-stat-value">{authorName}</span>
							</div>
							<div className="device-detection-stat-item">
								<span className="device-detection-stat-label">Total Devices Found:</span>
								<span className="device-detection-stat-value">{total_devices_found}</span>
							</div>
							<div className="device-detection-stat-item">
								<span className="device-detection-stat-label">Total Installed Devices Found:</span>
								<span className="device-detection-stat-value">{totalInstalledProducts}</span>
							</div>
							{!!categoriesCount && (
								<div className="device-detection-stat-item">
									<span className="device-detection-stat-label">Categories Detected:</span>
									<span className="device-detection-stat-value">{categoriesCount}</span>
								</div>
							)}
							<div className="device-detection-stat-item">
								<span className="device-detection-stat-label">Overall Confidence:</span>
								<span className={`device-detection-stat-value ${response.confidence_level}`}>
									{response.confidence_level?.toUpperCase()}
								</span>
							</div>
						</div>

						{categories_detected && categories_detected.length > 0 && (
							<div className="device-detection-analysis-notes">
								<span className="device-detection-subsection-title">Device Categories Detected</span>
								<div className="device-detection-categories-table-container">
									<Table
										// rowKey={r => `category-${r.category}`}
										dataSource={categories_detected}
										columns={tableColumns as TableColumnsType}
										loading={isLoading}
										pagination={false}
									/>
								</div>
							</div>
						)}

						{response.analysis_notes && (
							<div className="device-detection-analysis-notes">
								<span className="device-detection-subsection-title">Analysis Notes</span>
								<p className="device-detection-notes">{response.analysis_notes}</p>
							</div>
						)}

						{response.property_analysis?.building_layout_notes && (
							<div className="device-detection-analysis-notes">
								<span className="device-detection-subsection-title">Building Layout:</span>
								<p className="device-detection-notes">{response.property_analysis.building_layout_notes}</p>
								{response.property_analysis.identifiable_areas?.length > 0 && (
									<>
										<span className="device-detection-subsection-title">Identifiable Areas:</span>
										<div className="device-detection-areas-list">
											{response.property_analysis.identifiable_areas.map((area: string, idx: number) => (
												<span key={`identifiable_area-${idx}-${area}`} className="device-detection-area-tag">
													{area}
												</span>
											))}
										</div>
									</>
								)}
							</div>
						)}
					</Card>
				</div>
			);
		} catch (error) {
			console.error("Error parsing detection summary:", error);
			return null;
		}
	};

	const detectionResults = useMemo(() => {
		if (!productsGroupedByProductType || productsGroupedByProductType.length === 0) return null;

		return (
			<div className="device-detection-results-section">
				<span className="device-detection-section-title">
					Detected Devices &amp; Installations ({productsGroupedByProductType.length}{" "}
					{productsGroupedByProductType.length === 1 ? "category" : "categories"})
				</span>

				<div className="device-detection-categories-list">
					{productsGroupedByProductType.map((productTypeGroup: any, productTypeIndex: number) => (
						<Card
							key={`device-detection-category-${productTypeGroup.product_type?.name}-${productTypeIndex}`}
							className="device-detection-category-card"
						>
							{/* ── Category header ── */}
							<div className="device-detection-category-header">
								<div className="device-detection-category-title-section">
									<h3 className="device-detection-category-title">{productTypeGroup.product_type.label}</h3>
								</div>
								<div className="device-detection-category-stats">
									<span className="device-detection-category-stat">
										{productTypeGroup.totalProducts} {productTypeGroup.totalProducts === 1 ? "device" : "devices"}
									</span>
									<span className="device-detection-category-stat">
										{productTypeGroup.totalInstalledProducts}{" "}
										{productTypeGroup.totalInstalledProducts === 1 ? "installation" : "installations"}
									</span>
								</div>
							</div>

							{/* ── Devices in this category ── */}
							<div className="device-detection-devices-in-category">
								{productTypeGroup.products.map((product: any, productIndex: number) => {
									const productImgUri =
										product.photo_link ?? `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`;

									return (
										<div key={`product-${product.name}-${productIndex}`} className="device-detection-device-section">
											{/* Device info row */}
											<div className="device-detection-device-info">
												<div className="device-detection-device-header-row">
													<img
														src={productImgUri}
														alt="Device icon"
														className={
															"device-detection-device-header-img" + (product.photo_link ? "" : " placeholder")
														}
													/>
													<div className="device-detection-device-header-column">
														<span className="device-detection-device-name">{product.name}</span>
														{product.installed_products && product.installed_products.length > 0 && (
															<span className="device-detection-installations-count">
																{`${product.installed_products.length} installed`}
															</span>
														)}
													</div>
													<div className="device-detection-device-header-column right">
														<div className="device-detection-row matched">
															{product.id && product.id !== "null" ? (
																<div className="device-detection-device-matched-container">
																	<span className="device-detection-device-matched">Matched Product ID:</span>
																	<span className="device-detection-device-matched">{product.id}</span>
																	{product.match_confidence && (
																		<span className={`device-detection-confidence ${product.match_confidence}`}>
																			{product.match_confidence} confidence
																		</span>
																	)}
																</div>
															) : (
																<span className="device-detection-device-new">New</span>
															)}

															{job?.status === DeviceDetectionJobStatus.DRAFT ? (
																<div className="device-detection-row">
																	<img
																		alt="edit-attachment-icon"
																		src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
																		className="selected-file-icon"
																		onClick={() => handleOpenProductModal(product)}
																	/>
																	<RemovePopconfirm
																		title="Delete Detected Device"
																		description={<span>Are you sure you want to delete this detected device?</span>}
																		handleDeleteClick={() => handleConfirmProductDeletion(product)}
																	/>
																</div>
															) : null}
														</div>
													</div>
												</div>

												{product.description && (
													<p className="device-detection-device-description">{product.description}</p>
												)}
											</div>

											{/* Installations table */}
											{product.installed_products && product.installed_products.length > 0 && (
												<div className="device-detection-installations-table">
													<Table
														dataSource={product.installed_products.map((ip: any) => ({
															product,
															...ip
														}))}
														columns={installationsTableColumns}
														pagination={false}
														rowKey={r => r.draft_installed_product_id || r.id}
														size="small"
														expandable={{
															expandedRowRender: record => (
																<div className="device-detection-installation-expanded">
																	{record.product_notes && (
																		<div className="device-detection-expanded-row">
																			<span className="device-detection-expanded-label">Notes:</span>
																			<p className="device-detection-expanded-value">{record.product_notes}</p>
																		</div>
																	)}
																	{record.attachments && record.attachments.length > 0 && (
																		<div className="device-detection-expanded-row">
																			<span className="device-detection-expanded-label">
																				Resources ({record.attachments.length}):
																			</span>
																			<div className="device-detection-files-list">
																				{record.attachments.map((file: any, index: number) => {
																					const FileIcon = getFileIcon(file.mime_type || "document");
																					return (
																						<div
																							key={`${record.draft_installed_product_id}-${file.name}-${index}`}
																							className="device-detection-document"
																							onClick={() => handleOnClickAttachment(file)}
																						>
																							<FileIcon className="device-detection-document-icon" />
																							<span className="device-detection-document-label">{file.name}</span>
																						</div>
																					);
																				})}
																			</div>
																		</div>
																	)}
																</div>
															),
															rowExpandable: record =>
																record.product_notes || (record.attachments && record.attachments.length > 0)
														}}
													/>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</Card>
					))}
				</div>
			</div>
		);
	}, [
		productsGroupedByProductType,
		job,
		installationsTableColumns,
		handleOpenProductModal,
		handleConfirmProductDeletion,
		handleOpenInstalledProductModal,
		handleConfirmInstalledProductDeletion,
		handleOnClickAttachment,
		getFileIcon
	]);

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div className="device-detection-container">

			<span className="page-title">Device Detection</span>

			<div className="device-detection-content-container">
				{!processingStarted ? (
					<>
						<Form className="device-detection-form">
							<span className="device-detection-form-title">Select a Property</span>

							<Form.Item
								className="device-detection-form-item"
								rules={[{ required: true, message: "Please select a property" }]}
								required
							>
								<Select
									value={selectedProperty ? JSON.stringify(selectedProperty) : undefined}
									className="device-detection-select"
									onChange={value => setSelectedProperty(JSON.parse(value))}
									options={propertyOptions}
									placeholder="Select Property"
									allowClear
									disabled={isLoading}
									loading={propertiesLoading}
									suffixIcon={
										<img
											src={`${process.env.PUBLIC_URL}/assets/images/icons/black-chevron-down.svg`}
											alt="dropdown icon"
											className="device-detection-chevron-button"
										/>
									}
								/>
							</Form.Item>

							<Divider />

							<span className="device-detection-form-title">Add Resources</span>
							<p className="device-detection-description">
								Upload any videos, photos, or documents from the property to include in automatic device detection.
							</p>

							<div className="device-detection-upload-section">
								<Button
									className="device-detection-upload-button"
									onClick={() => setAddAttachmentModalOpen(true)}
									disabled={isLoading}
								>
									Add Files
								</Button>
							</div>

							{attachmentsFiles.length > 0 && (
								<div className="device-detection-attachments-container">
									{attachmentsFiles.map(attachmentFile => {
										const uid = attachmentFile.uid || attachmentFile.id;
										return (
											<AttachmentItem
												key={`detection-attachment-${uid}`}
												name={attachmentFile.name}
												file={attachmentFile.file}
												type={attachmentFile.type}
												onRemove={() => {
													if (!isLoading) handleOnRemoveAttachment(attachmentFile);
												}}
											/>
										);
									})}
								</div>
							)}
						</Form>
						<div className="device-detection-button-container">
							<Button
								className="device-detection-button"
								onClick={handleOnSubmit}
								disabled={isLoading || !selectedProperty || attachmentsFiles.length === 0}
							>
								Start automagical detection
							</Button>
						</div>
					</>
				) : (
					<div className="device-detection-processing-container">
						{job?.status === DeviceDetectionJobStatus.DRAFT || job?.status === DeviceDetectionJobStatus.PUBLISHED ? (
							<div className="device-detection-results-container">
								{renderDetectionSummary()}
								{detectionResults}
								<div className="hdd-add-product-btn-container">
									<button className="hdd-add-install-btn" onClick={handleOpenAddProductModal}>
										<img
											src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle-purple.svg`}
											alt="Add"
											className="hdd-add-install-btn-icon"
										/>
										Add product
									</button>
								</div>
								{job?.status === DeviceDetectionJobStatus.DRAFT && (
									<div className="device-detection-button-container">
										<Button
											className="device-detection-button"
											onClick={publishDetection}
											loading={isLoading}
											disabled={isLoading}
										>
											Accept &amp; publish
										</Button>
									</div>
								)}
							</div>
						) : job?.status === DeviceDetectionJobStatus.FAILED ? (
							<Card className="device-detection-error-card">
								<h2>Detection Failed</h2>
								<p>An error occurred during device detection</p>
								<Button
									className="device-detection-retry-button"
									onClick={() => {
										setProcessingStarted(false);
										setJobId(null);
										setAttachmentsFiles([]);
									}}
								>
									Try Again
								</Button>
							</Card>
						) : (
							<Card className="device-detection-processing-card">
								{(processingData?.status === DeviceDetectionJobStatus.PENDING ||
									processingData?.status === DeviceDetectionJobStatus.PROCESSING) && (
									<>
										<Spin size="large" />
										<h2>Processing Device Detection...</h2>
										<p>Status: {processingData?.status || "initializing"}</p>
										<p>This may take a few minutes depending on uploaded file size</p>
									</>
								)}
							</Card>
						)}
					</div>
				)}
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
				filesLimit={20}
			/>

			<NewProductModal
				open={productModalOpen}
				onCancel={handleCloseProductModal}
				onSuccess={handleCloseProductModal}
				detectedDevice={selectedProduct}
				onUpdateProduct={core.updateProduct}
				onClickAttachment={handleOnClickAttachment}
			/>

			<NewProductModal
				open={addProductModalOpen}
				onCancel={handleCloseAddProductModal}
				onSuccess={handleCloseAddProductModal}
				onAddProduct={core.addProduct}
				onClickAttachment={handleOnClickAttachment}
			/>

			<NewInstalledProductModal
				open={installedProductModalOpen}
				onCancel={handleCloseInstalledProductModal}
				onSuccess={handleCloseInstalledProductModal}
				property={selectedProperty}
				detectedInstallation={selectedInstallation}
				onUpdateInstalledProduct={core.updateInstalledProduct}
				onClickAttachment={handleOnClickAttachment}
			/>

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>

			<LoadingOverlay isLoading={isLoading} tip={"Uploading files"} />
		</div>
	);
};

export default DeviceDetectionJob;
